import React, { FC, useCallback, useContext, useMemo } from 'react';
import { apiPermission } from '../resources/api/apiPermission';
import { createFetcher } from '../contexts/Fetcher';
import { UrlQueryDataGrid } from '../components/UrlQueryDataGrid/UrlQueryDataGrid';
import { GridColumns } from '@mui/x-data-grid';
import { IEndpointPermission } from '../resources/models/IEndpointPermission';
import {
  adminRoles,
  EAdminRole,
  EOrganizationRole,
  EUserRole,
  organizationRoles,
  TRolesCollection,
  userRoles,
} from '../const/roles';
import { EasyFilter } from '../components/EasyFilter/EasyFilter';
import { useFreeTextFilter } from '../hooks/useFreeTextFilter';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { styled } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useQueryParam } from '../hooks/useQueryParam';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { useCloseDialog } from '../hooks/useCloseDialog';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

const { usePermissions } = apiPermission;

const BlankBox = styled(CheckBoxOutlineBlank)({
  opacity: 0.4,
});

const PermissionFetcher = createFetcher(() => {
  const { execute } = usePermissions();

  return useCallback(async () => {
    const permissions = [];
    let page = 1;
    let total = 0;
    do {
      const result = await execute({
        page,
        limit: 100,
      });
      total = result.payload.total_items;
      permissions.push(...result.payload.data);
      ++page;
    } while (permissions.length < total);

    return permissions;
  }, [execute]);
});

interface IEndpoint {
  method: string;
  path: string;
  id: string;
  isOrganizationMemberAllowed: boolean;
  isOrganizationAdminAllowed: boolean;
  isPixotopeAdminAllowed: boolean;
}

const isRoleAllowed = <EGroup extends any>(
  permission: IEndpointPermission,
  roleSet: TRolesCollection<EGroup>,
  role: EGroup
) => {
  return permission.policies.some((policy) => {
    return policy.member_of_roles
      ?.map((policyRoleId) => roleSet.find((role) => role.id === policyRoleId)?.role)
      ?.includes(role);
  });
};

const parseEndpointPermissions = (method: string, id: string, permissions?: IEndpointPermission[]): IEndpoint[] => {
  if (!permissions) return [];
  return permissions.map((permission) => {
    return {
      path: permission.path,
      method,
      id,
      isAnonymousAllowed: isRoleAllowed(permission, userRoles, EUserRole.Anonymous),
      isUserAllowed: isRoleAllowed(permission, userRoles, EUserRole.User),
      isOrganizationMemberAllowed: isRoleAllowed(permission, organizationRoles, EOrganizationRole.Member),
      isOrganizationAdminAllowed: isRoleAllowed(permission, organizationRoles, EOrganizationRole.Admin),
      isPixotopeSalesAllowed: isRoleAllowed(permission, adminRoles, EAdminRole.Sales),
      isPixotopeAdminAllowed: isRoleAllowed(permission, adminRoles, EAdminRole.Admin),
    };
  });
};

const PermissionsComponent: FC = () => {
  const { data } = useContext(PermissionFetcher.Context);
  const detailsId = useQueryParam('details');

  const details = detailsId && data ? data.find((row) => row.id === detailsId) : void 0;
  const closeDialog = useCloseDialog(void 0, ['details']);

  const rows = useMemo(() => {
    if (!data) return [] as IEndpoint[];
    return data.reduce((endpoints, current) => {
      return [
        ...endpoints,
        ...parseEndpointPermissions('GET', current.id, current.endpoints.GET),
        ...parseEndpointPermissions('POST', current.id, current.endpoints.POST),
        ...parseEndpointPermissions('DELETE', current.id, current.endpoints.DELETE),
        ...parseEndpointPermissions('PUT', current.id, current.endpoints.PUT),
        ...parseEndpointPermissions('PATCH', current.id, current.endpoints.PATCH),
      ];
    }, [] as IEndpoint[]);
  }, [data]);

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'method',
        headerName: 'method',
        width: 120,
      },
      {
        field: 'path',
        headerName: 'path',
        flex: 1,
      },
      {
        field: 'id',
        headerName: 'id',
        flex: 1,
      },
      {
        field: 'isAnonymousAllowed',
        headerName: 'anonymous',
        width: 40,
        renderCell: ({ value }) => (value ? <CheckBox /> : <BlankBox />),
      },
      {
        field: 'isUserAllowed',
        headerName: 'user',
        width: 40,
        renderCell: ({ value }) => (value ? <CheckBox /> : <BlankBox />),
      },
      {
        field: 'isOrganizationMemberAllowed',
        headerName: 'member',
        width: 40,
        renderCell: ({ value }) => (value ? <CheckBox /> : <BlankBox />),
      },
      {
        field: 'isOrganizationAdminAllowed',
        headerName: 'owner',
        width: 40,
        renderCell: ({ value }) => (value ? <CheckBox /> : <BlankBox />),
      },
      {
        field: 'isPixotopeSalesAllowed',
        headerName: 'sales',
        width: 40,
        renderCell: ({ value }) => (value ? <CheckBox /> : <BlankBox />),
      },
      {
        field: 'isPixotopeAdminAllowed',
        headerName: 'admin',
        width: 40,
        renderCell: ({ value }) => (value ? <CheckBox /> : <BlankBox />),
      },
    ],
    []
  );
  const filteredRows = useFreeTextFilter(rows, columns);
  const { search } = useLocation();

  if (!data) return null;

  return (
    <div>
      <EasyFilter />
      <UrlQueryDataGrid
        columns={columns}
        rows={filteredRows}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        getRowClassName={(params) => (params.row.archived ? 'Mui-disabled' : '')}
        getRowLink={(params) => {
          const newSearch = new URLSearchParams(search);
          newSearch.set('details', params.row.id);
          return `?${newSearch}`;
        }}
        getRowId={(params) => `${params.method}-${params.id}-${params.path}`}
      />
      {details ? (
        <Dialog open={true} onClose={closeDialog} fullWidth={true}>
          <DialogTitle>{detailsId}</DialogTitle>
          <DialogContent>
            <pre>{JSON.stringify(details, null, 2)}</pre>
          </DialogContent>
          <DialogActions>
            <Button variant={'contained'} onClick={closeDialog}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </div>
  );
};

export const PermissionsPage: FC = () => {
  return (
    <PermissionFetcher.WAF>
      <PermissionsComponent />
    </PermissionFetcher.WAF>
  );
};
